/**
 * DocumentHeader — Carte d'en-tête partagée pour OR / Devis / Facture (iter127).
 *
 * Layout commun :
 *   [Icon]  [Numéro + Badges]    [Total TTC]
 *           [contenu spécifique  (slot children)]
 *
 * Props :
 *   - icon          : composant lucide-react (ex: Wrench, FileText)
 *   - iconColor     : classe Tailwind background pour la pastille (default "#52a675")
 *   - numero        : string (ex: "2026-2781", "DEV-2026-0123", "F-2026-0042")
 *   - testidNumero  : data-testid pour le code numéro
 *   - badges        : React.Node (badges statut/paiement/archivé…) rendus à droite du numéro
 *   - extraTopRight : React.Node optionnel (ex: Select type tarif sur OR)
 *   - totalTtc      : number (ex: 1234.56)
 *   - monnaie       : string (default "CHF")
 *   - resteAPayer   : number optionnel — si > 0.01, affiché en jaune sous le total
 *   - children      : contenu central (client, véhicule, dates, références…)
 */
import { Card } from "@/components/ui/card";

export default function DocumentHeader({
  icon: Icon,
  iconBgClass = "bg-[#52a675]/10",
  iconColorClass = "text-[#52a675]",
  numero,
  numeroPrefix = null,
  testidNumero,
  badges = null,
  extraTopRight = null,
  totalTtc,
  monnaie = "CHF",
  resteAPayer = null,
  children = null,
  className = "",
  testidRoot = "document-header",
  bare = false,  // iter127 — si true, pas de Card wrapper (utilisé par OrHeader qui partage sa Card avec OrActionsBar/VehiculeCard)
}) {
  const inner = (
    <div className="flex items-start gap-3 sm:gap-4">
      {Icon && (
        <div className={`w-10 h-10 sm:w-14 sm:h-14 rounded-lg ${iconBgClass} ${iconColorClass} flex items-center justify-center flex-shrink-0`}>
          <Icon className="w-5 h-5 sm:w-7 sm:h-7" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-x-2 gap-y-1 flex-wrap mb-1">
          <code
            className="text-xl sm:text-2xl font-bold font-mono text-gray-900 leading-none"
            data-testid={testidNumero}
          >
            {numeroPrefix ? `${numeroPrefix} ${numero}` : numero}
          </code>
          {badges}
          {extraTopRight}
        </div>
        {children}
      </div>
      {totalTtc != null && (
        <div className="text-right flex-shrink-0 self-start">
          <div className="text-[10px] uppercase tracking-wider text-gray-500">Total TTC</div>
          <div className="text-lg sm:text-2xl font-bold font-mono text-gray-900 leading-tight whitespace-nowrap" data-testid="document-header-total-ttc">
            {(totalTtc || 0).toLocaleString("fr-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div className="text-[10px] sm:text-xs text-gray-500">{monnaie}</div>
          {resteAPayer != null && resteAPayer > 0.01 && (
            <div className="text-[10px] sm:text-xs text-amber-700 mt-1 font-semibold whitespace-nowrap" data-testid="document-header-reste">
              Reste : {resteAPayer.toLocaleString("fr-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {monnaie}
            </div>
          )}
        </div>
      )}
    </div>
  );
  if (bare) {
    return <div data-testid={testidRoot} className={className}>{inner}</div>;
  }
  return (
    <Card className={`p-5 border-gray-200 mb-4 ${className}`} data-testid={testidRoot}>
      {inner}
    </Card>
  );
}
